<script setup lang="ts">
import { computed, toValue } from 'vue';
import type { SortDirection } from '@/types/tableTypes';
import { injectCardSchedules, provideTableScheduleSort, provideTableSchedules } from './cardSchedulesContext';
import type { ScheduleEntry } from './scheduleGroups';
import { tableSchedulesFor } from './useCardSchedules';

/**
 * Gives the table inside it the sort its rows were searched with, so each row's schedule
 * cells order their lines by it. See useTableSchedules. It also orders and cuts each row's
 * lines once, from the page's card schedules, so a row's cells share one array.
 *
 * The search page wraps only its table in it. The cards and the split view's record pane
 * sit outside it, so they keep the card order whatever the sort.
 */
const props = defineProps<{
    /** The sort the search sent, keyed by field in priority order. */
    sort: Readonly<Record<string, SortDirection>> | null | undefined;
}>();

provideTableScheduleSort(() => props.sort);

const pageCardSchedules = injectCardSchedules();
const tableSchedulesByRowId = computed<ReadonlyMap<string, ScheduleEntry[]>>(() => {
    const byRowId = new Map<string, ScheduleEntry[]>();
    if (!pageCardSchedules) return byRowId;
    for (const [rowId, entries] of toValue(pageCardSchedules)) {
        byRowId.set(rowId, tableSchedulesFor(entries, props.sort));
    }
    return byRowId;
});
provideTableSchedules(tableSchedulesByRowId);
</script>

<template>
    <slot />
</template>
